import { UserIcon } from '@heroicons/react/24/outline';
import parse from 'html-react-parser';
import { useMemo, useState } from 'react';

import { Badge } from '@/components/core/Badge';
import { FormattedDate } from '@/components/core/FormattedDate';
import { Icon } from '@/components/core/Icon';
import LabelValue from '@/components/core/LabelValue/LabelValue';
import { Loader } from '@/components/core/Loader';
import FileTypeIcon from '@/components/download-button/FileTypeIcon';
import { useTranslation } from '@/hooks/useTranslation';

import { type TaskFormApi,TaskInteractiveDetails } from './TaskInteractiveDetails';
import {
    commentsOldestFirst,
    downloadUrl,
    type EditorTask,
    EMPTY_TASK_VALUE,
    fileTypeIconName,
    isImageAttachment,
    sanitizeTaskHtml,
    type TaskAttachment,
    taskStatusBadgeVariant,
    userDisplayName,
    userImageUrl,
} from './taskView';
import { useTaskComments } from './taskView.queries';
import { TaskCommentComposer, TaskRelatedItems, TaskWorkflow, type TaskWorkflowApi } from './TaskViewActions';

type TaskViewInEditorProps = {
    task: EditorTask;
    /** When false, the title is omitted so a dialog header can own it. */
    showTitle?: boolean;
    /** Workflow, related items, and the new-comment form from the task page. */
    interactive?: boolean;
    onTaskUpdated?: (task: EditorTask) => void;
    onFormBind?: (api: TaskFormApi) => void;
    onWorkflowBind?: (api: TaskWorkflowApi) => void;
    onBrowse?: (search: Record<string, string>) => void;
};

const commentLinkClassName = 'tw-text-t-primary tw-underline focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ring focus-visible:tw-ring-offset-2 tw-rounded';

const CommentAvatar = ({ author }: { author?: string }) => {
    const [failed, setFailed] = useState(false);
    const imageUrl = !failed ? userImageUrl(author) : undefined;

    return (
        <span className="tw-inline-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full tw-bg-fill-secondary lg:tw-h-10 lg:tw-w-10">
            {imageUrl ? (
                <img
                    src={imageUrl}
                    alt=""
                    className="tw-h-full tw-w-full tw-object-cover"
                    onError={() => setFailed(true)}
                />
            ) : (
                <Icon
                    icon={UserIcon}
                    variant="secondary"
                    className="tw-h-4 tw-w-4 lg:tw-h-5 lg:tw-w-5"
                />
            )}
        </span>
    );
};

const CommentAttachment = ({ file }: { file: TaskAttachment }) => {
    if (isImageAttachment(file.contentType)) {
        return (
            <div className="tw-mt-2">
                <a href={file.url} className={`tw-inline-block ${commentLinkClassName}`}>
                    <img
                        src={file.url}
                        alt={file.name}
                        className="tw-max-h-48 tw-max-w-full tw-object-contain"
                    />
                </a>
                <div className="tw-text-t-xs lg:tw-text-t-sm tw-text-t-inactive">{file.name}</div>
            </div>
        );
    }

    return (
        <div className="tw-mt-2">
            <a
                href={downloadUrl(file.url)}
                className={`tw-inline-flex tw-items-center tw-gap-2 tw-text-t-sm lg:tw-text-t-base ${commentLinkClassName}`}
            >
                <FileTypeIcon fileType={fileTypeIconName(file.name)} />
                {file.name}
            </a>
        </div>
    );
};

const TaskViewInEditor = ({
    task,
    showTitle = true,
    interactive = false,
    onTaskUpdated,
    onFormBind,
    onWorkflowBind,
    onBrowse,
}: TaskViewInEditorProps) => {
    const { t } = useTranslation();
    const { data: loadedComments = [], isPending } = useTaskComments({
        variables: { taskId: task.id },
        enabled: task.id != null && task.id !== '',
    });
    const comments = useMemo(() => commentsOldestFirst(loadedComments), [loadedComments]);
    const notesHtml = sanitizeTaskHtml(task.notes);
    const assignee = userDisplayName(task.assignee);

    return (
        <section
            aria-label={showTitle ? undefined : task.title}
            aria-labelledby={showTitle ? 'task-view-in-editor-title' : undefined}
            className="tw-flex tw-flex-col tw-gap-4"
        >
            {showTitle && (
                <h1
                    id="task-view-in-editor-title"
                    className="tw-m-0 tw-text-t-lg tw-font-bold tw-text-h-primary lg:tw-text-t-xl"
                >
                    {task.title}
                </h1>
            )}
            <div className="tw-flex tw-flex-col tw-gap-3">
                {interactive ? (
                    <TaskInteractiveDetails task={task} onTaskUpdated={onTaskUpdated} onFormBind={onFormBind} />
                ) : (
                    <>
                        <LabelValue
                            label={t('TASK.DUE_DATE')}
                            value={task.dueDate
                                ? <FormattedDate value={task.dueDate} format="fullDate" />
                                : EMPTY_TASK_VALUE}
                            valueClassName="tw-whitespace-normal"
                        />
                        <LabelValue
                            label={t('CREATED_AT')}
                            value={(
                                <>
                                    <FormattedDate value={task.createDate} format="fullDate" />
                                    {' '}
                                    {t('TIME.AT')}
                                    {' '}
                                    <FormattedDate value={task.createDate} format="mediumTime" />
                                </>
                            )}
                            valueClassName="tw-whitespace-normal"
                        />
                        <LabelValue
                            label={t('TASK.AUTHOR')}
                            value={userDisplayName(task.author) || '\u00a0'}
                            valueClassName="tw-whitespace-normal"
                        />
                        <LabelValue
                            label={t('TASK.ASSIGNEE')}
                            value={assignee || EMPTY_TASK_VALUE}
                            valueClassName="tw-whitespace-normal"
                        />
                        <div className="tw-flex tw-flex-col tw-items-start tw-gap-1 lg:tw-flex-row lg:tw-items-baseline lg:tw-gap-2">
                            <span className="tw-whitespace-nowrap tw-text-t-xs tw-text-t-secondary lg:tw-text-t-sm">
                                {t('TASK.NOTES')}
                            </span>
                            <div className="tw-min-w-0 tw-text-t-sm tw-text-t-primary lg:tw-text-t-base">
                                {notesHtml ? parse(notesHtml) : EMPTY_TASK_VALUE}
                            </div>
                        </div>
                        <LabelValue
                            label={t('TASK.CHANGE_STATUS')}
                            value={(
                                <Badge variant={taskStatusBadgeVariant(task.taskStatus)}>
                                    {task.taskStatus}
                                </Badge>
                            )}
                        />
                    </>
                )}
                {interactive && (
                    <TaskWorkflow task={task} onTaskUpdated={onTaskUpdated} onWorkflowBind={onWorkflowBind} />
                )}
            </div>
            {interactive && (
                <TaskRelatedItems
                    task={task}
                    onBrowse={onBrowse}
                />
            )}
            {isPending && <Loader />}
            {comments.length > 0 && (
                <div className="tw-flex tw-flex-col tw-gap-3">
                    <h2 className="tw-m-0 tw-text-t-base tw-font-bold tw-text-h-primary lg:tw-text-t-lg">
                        {t('TASK.COMMENTS.LABEL')}
                    </h2>
                    <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-4 tw-p-0">
                        {comments.map((comment, index) => (
                            <li key={comment.id ?? `${comment.author}-${comment.createDate}-${index}`}>
                                <article className="tw-flex tw-gap-3">
                                    <CommentAvatar author={comment.author} />
                                    <div className="tw-min-w-0">
                                        <strong className="tw-text-t-sm tw-font-bold tw-text-t-primary lg:tw-text-t-base">
                                            {userDisplayName(comment.author)}
                                        </strong>
                                        <div className="tw-text-t-xs tw-text-t-inactive lg:tw-text-t-sm">
                                            Posted
                                            {' '}
                                            <FormattedDate value={comment.createDate} format="fullDate" />
                                            {' '}
                                            {t('TIME.AT')}
                                            {' '}
                                            <FormattedDate value={comment.createDate} format="mediumTime" />
                                        </div>
                                        {comment.text && (
                                            <div className="tw-mt-1 tw-text-t-sm tw-text-t-primary lg:tw-text-t-base">
                                                {parse(sanitizeTaskHtml(comment.text))}
                                            </div>
                                        )}
                                        {comment.assets?.map((file) => (
                                            <CommentAttachment key={`${file.url}-${file.name}`} file={file} />
                                        ))}
                                    </div>
                                </article>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {interactive && <TaskCommentComposer task={task} />}
        </section>
    );
};

export { TaskViewInEditor };
export type { TaskViewInEditorProps };
